import "core-js/modules/es.array.includes.js";
import PropTypes from "prop-types";
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { useEuiTheme } from '../../services';
import { euiFlexGroupStyles } from './flex_group.styles';
export var GUTTER_SIZES = ['none', 'xs', 's', 'm', 'l', 'xl'];
export var ALIGN_ITEMS = ['stretch', 'flexStart', 'flexEnd', 'center', 'baseline'];
export var JUSTIFY_CONTENTS = ['flexStart', 'flexEnd', 'center', 'spaceBetween', 'spaceAround', 'spaceEvenly'];
export var DIRECTIONS = ['row', 'rowReverse', 'column', 'columnReverse'];
var isValidElement = function isValidElement(component) {
  return ['div', 'span'].includes(component);
};
export var EuiFlexGroup = forwardRef(({
  children,
  className,
  gutterSize = 'l',
  alignItems = 'stretch',
  responsive = true,
  justifyContent = 'flexStart',
  direction = 'row',
  wrap = false,
  component = 'div',
  ...rest
}, ref) => {
  const euiTheme = useEuiTheme();
  const styles = euiFlexGroupStyles(euiTheme);
  const cssStyles = [styles.euiFlexGroup, responsive && !direction.includes('column') && styles.responsive, wrap && styles.wrap, styles.gutterSizes[gutterSize], styles.justifyContent[justifyContent], styles.alignItems[alignItems], styles.direction[direction]];
  const classes = classNames('euiFlexGroup', className);
  if (!isValidElement(component)) {
    throw new Error(`${component} is not a valid element type. Use \`div\` or \`span\`.`);
  }
  return component === 'span' ? <span css={cssStyles} className={classes} ref={ref} {...rest}>
        {children}
      </span> : <div css={cssStyles} className={classes} ref={ref} {...rest}>
        {children}
      </div>;
});
EuiFlexGroup.propTypes = {
  alignItems: PropTypes.any,
  component: PropTypes.oneOf(["div", "span"]),
  direction: PropTypes.any,
  gutterSize: PropTypes.any,
  justifyContent: PropTypes.any,
  responsive: PropTypes.bool,
  wrap: PropTypes.bool,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any
};
EuiFlexGroup.displayName = 'EuiFlexGroup';
try {
  EuiFlexGroup.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/flex/flex_group.tsx',
    description: '',
    displayName: 'EuiFlexGroup',
    methods: [],
    props: {
      alignItems: {
        defaultValue: {
          value: 'stretch'
        },
        description: '',
        name: 'alignItems',
        parent: {
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"center" | "baseline" | "stretch" | "flexStart" | "flexEnd"',
          value: [{
            value: '"center"'
          }, {
            value: '"baseline"'
          }, {
            value: '"stretch"'
          }, {
            value: '"flexStart"'
          }, {
            value: '"flexEnd"'
          }]
        }
      },
      component: {
        defaultValue: {
          value: 'div'
        },
        description: '',
        name: 'component',
        parent: {
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'FlexGroupComponentType',
          value: [{
            value: '"div"'
          }, {
            value: '"span"'
          }]
        }
      },
      direction: {
        defaultValue: {
          value: 'row'
        },
        description: '',
        name: 'direction',
        parent: {
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"row" | "column" | "rowReverse" | "columnReverse"',
          value: [{
            value: '"row"'
          }, {
            value: '"column"'
          }, {
            value: '"rowReverse"'
          }, {
            value: '"columnReverse"'
          }]
        }
      },
      gutterSize: {
        defaultValue: {
          value: 'l'
        },
        description: '',
        name: 'gutterSize',
        parent: {
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "none" | "m" | "xs" | "l" | "xl"',
          value: [{
            value: '"s"'
          }, {
            value: '"none"'
          }, {
            value: '"m"'
          }, {
            value: '"xs"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }]
        }
      },
      justifyContent: {
        defaultValue: {
          value: 'flexStart'
        },
        description: '',
        name: 'justifyContent',
        parent: {
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"center" | "flexStart" | "flexEnd" | "spaceBetween" | "spaceAround" | "spaceEvenly"',
          value: [{
            value: '"center"'
          }, {
            value: '"flexStart"'
          }, {
            value: '"flexEnd"'
          }, {
            value: '"spaceBetween"'
          }, {
            value: '"spaceAround"'
          }, {
            value: '"spaceEvenly"'
          }]
        }
      },
      responsive: {
        defaultValue: {
          value: 'true'
        },
        description: '',
        name: 'responsive',
        parent: {
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      wrap: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'wrap',
        parent: {
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        },
        declarations: [{
          fileName: 'app/src/components/flex/flex_group.tsx',
          name: 'EuiFlexGroupProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@emotion/react/types/css-prop.d.ts',
          name: 'Attributes'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      ref: {
        defaultValue: null,
        description: 'Allows getting a ref to the component instance.\n' + 'Once the component unmounts, React will set `ref.current` to `null` (or call the ref with `null` if you passed a callback ref).\n' + '@see https://react.dev/learn/referencing-values-with-refs#refs-and-the-dom',
        name: 'ref',
        parent: {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'RefAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'RefAttributes'
        }],
        required: false,
        type: {
          name: 'Ref<HTMLDivElement | HTMLSpanElement>'
        }
      }
    },
    extendedInterfaces: ['EuiFlexGroupProps', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'Attributes']
  };
} catch (e) {}